import React, { useState, useEffect } from 'react';
import {
  HomeIcon,
  MagnifyingGlassIcon,
  SpeakerWaveIcon,
  BuildingOfficeIcon,
  CalendarDaysIcon,
  Cog6ToothIcon,
  ChartBarIcon,
  BuildingStorefrontIcon,
  DocumentTextIcon,
  SparklesIcon,
  KeyIcon,
  Bars3Icon,
  XMarkIcon,
  ArrowRightOnRectangleIcon,
  BoltIcon
} from '@heroicons/react/24/outline';

const sections = [
  { name: 'SuperGenie Dashboard', icon: HomeIcon },
  { name: 'Lead Generation', icon: MagnifyingGlassIcon },
  { name: 'Outreach Automation', icon: SpeakerWaveIcon },
  { name: 'CRM & Pipeline', icon: BuildingOfficeIcon },
  { name: 'Visual Workflow Builder', icon: BoltIcon },
  { name: 'Appointments', icon: CalendarDaysIcon },
  { name: 'API Keys & Integrations', icon: KeyIcon },
  { name: 'Resources & Docs', icon: DocumentTextIcon },
  { name: 'White-Label SaaS', icon: BuildingStorefrontIcon }
];

export default function Sidebar({ activeSection, onSelect, isDarkMode, toggleDarkMode, onLogout }) {
  const [isCollapsed, setIsCollapsed] = useState(false);

  const toggleSidebar = () => {
    const nextCollapsed = !isCollapsed;
    setIsCollapsed(nextCollapsed);
    document.documentElement.style.setProperty('--sidebar-width', nextCollapsed ? '72px' : '224px');
  };

  // Set initial width
  useEffect(() => {
    document.documentElement.style.setProperty('--sidebar-width', isCollapsed ? '72px' : '224px');
  }, [isCollapsed]);

  const handleSectionSelect = (sectionName) => {
    onSelect(sectionName);
  };

  return (
    <>
      <aside className={`
        fixed top-0 left-0 z-40 h-screen
        ${isCollapsed ? 'w-[72px]' : 'w-56'}
        bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900
        border-r border-gray-700
        shadow-xl
        transition-all duration-300 ease-in-out
        flex flex-col
      `}>
        {/* Sidebar Header */}
        <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'} px-3 py-4 border-b border-gray-700`}>
          {!isCollapsed && (
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-genie-teal to-genie-orange flex items-center justify-center shadow-glow flex-shrink-0">
                <SparklesIcon className="w-5 h-5 text-white" />
              </div>
              <div className="min-w-0">
                <h2 className="text-[15px] font-semibold text-white tracking-tight truncate">Market Genie</h2>
                <p className="text-[11px] text-slate-400 mt-0.5 truncate">AI Marketing Platform</p>
              </div>
            </div>
          )}

          {/* Collapse toggle */}
          <button
            onClick={toggleSidebar}
            className={`text-slate-400 hover:text-white hover:bg-white/10 transition-colors duration-200 p-2 rounded-lg ${
              isCollapsed ? 'w-9 h-9 flex items-center justify-center' : ''
            }`}
            aria-label="Toggle sidebar"
          >
            {isCollapsed ? (
              <Bars3Icon className="w-5 h-5" />
            ) : (
              <XMarkIcon className="w-5 h-5" />
            )}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto px-2.5 py-4">
          {!isCollapsed && (
            <p className="px-3 pb-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
              Workspace
            </p>
          )}
          <ul className="space-y-1">
            {sections.map(section => {
              const Icon = section.icon;
              const isActive = activeSection === section.name;
              return (
                <li key={section.name}>
                  <button
                    onClick={() => handleSectionSelect(section.name)}
                    className={`relative w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all duration-200 group ${
                      isActive
                        ? 'bg-gradient-to-r from-teal-600 to-teal-500 text-white shadow-lg'
                        : 'text-gray-300 hover:text-white hover:bg-gray-800'
                    } ${isCollapsed ? 'justify-center' : ''}`}
                    title={isCollapsed ? section.name : ''}
                  >
                    {/* Active indicator */}
                    {isActive && (
                      <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 rounded-full bg-white/90" />
                    )}
                    <Icon className={`w-[18px] h-[18px] flex-shrink-0 ${
                      isActive ? 'text-white' : 'text-slate-400 group-hover:text-teal-300'
                    }`} />
                    {!isCollapsed && (
                      <span className={`text-sm font-medium truncate ${isActive ? 'text-white' : ''}`}>
                        {section.name}
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Bottom Controls */}
        <div className="px-2.5 py-4 border-t border-gray-700">
          <div className="space-y-1">
            {/* Dark Mode Toggle */}
            <button
              onClick={toggleDarkMode}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-colors duration-200 hover:bg-white/[0.06] text-slate-300 hover:text-white group ${
                isCollapsed ? 'justify-center' : ''
              }`}
              title={isCollapsed ? (isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode') : ''}
            >
              <span className="w-[18px] h-[18px] flex-shrink-0 flex items-center justify-center text-base">
                {isDarkMode ? '☀️' : '🌙'}
              </span>
              {!isCollapsed && (
                <span className="text-sm font-medium">
                  {isDarkMode ? 'Light Mode' : 'Dark Mode'}
                </span>
              )}
            </button>

            {/* Logout */}
            <button
              onClick={onLogout}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-colors duration-200 hover:bg-red-500/10 text-slate-300 hover:text-red-300 group ${
                isCollapsed ? 'justify-center' : ''
              }`}
              title={isCollapsed ? 'Logout' : ''}
            >
              <ArrowRightOnRectangleIcon className="w-[18px] h-[18px] flex-shrink-0 text-slate-400 group-hover:text-red-400" />
              {!isCollapsed && (
                <span className="text-sm font-medium">Logout</span>
              )}
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}
